(() => {
  'use strict';

  const selectors = {
    focusable: 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  };

  const state = {
    activeLayer: null,
    activeTrigger: null,
    trapCleanup: null
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const debounce = (callback, wait = 250) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
  };

  const announce = (message) => {
    const region = document.querySelector('[data-live-region]');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  };

  const getFocusable = (container) => Array.from(container.querySelectorAll(selectors.focusable))
    .filter((element) => !element.hasAttribute('hidden') && element.getClientRects().length > 0);

  const trapFocus = (container) => {
    const keyHandler = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(container);
      if (!focusable.length) {
        event.preventDefault();
        container.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', keyHandler);
    return () => container.removeEventListener('keydown', keyHandler);
  };

  const setExpanded = (trigger, expanded) => {
    if (trigger?.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', String(expanded));
  };

  const openLayer = (layer, trigger) => {
    if (!layer) return;
    if (state.activeLayer && state.activeLayer !== layer) closeLayer(state.activeLayer, false);
    const backdrop = document.querySelector('[data-overlay-backdrop]');
    state.activeLayer = layer;
    state.activeTrigger = trigger || document.activeElement;
    layer.hidden = false;
    backdrop?.removeAttribute('hidden');
    document.body.classList.add('overflow-hidden');
    document.querySelector('.site-header')?.classList.add('is-menu-open');
    setExpanded(trigger, true);
    const panel = layer.querySelector('[role="dialog"]') || layer;
    state.trapCleanup?.();
    state.trapCleanup = trapFocus(panel);
    window.requestAnimationFrame(() => {
      const autofocus = layer.querySelector('[autofocus], input[type="search"]');
      (autofocus || getFocusable(panel)[0] || panel).focus({ preventScroll: true });
    });
  };

  const closeLayer = (layer = state.activeLayer, restoreFocus = true) => {
    if (!layer) return;
    const backdrop = document.querySelector('[data-overlay-backdrop]');
    layer.hidden = true;
    backdrop?.setAttribute('hidden', '');
    document.body.classList.remove('overflow-hidden');
    document.querySelector('.site-header')?.classList.remove('is-menu-open');
    state.trapCleanup?.();
    state.trapCleanup = null;
    setExpanded(state.activeTrigger, false);
    if (restoreFocus && state.activeTrigger?.isConnected) state.activeTrigger.focus({ preventScroll: true });
    state.activeLayer = null;
    state.activeTrigger = null;
  };

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.classList.toggle('is-empty', Number(count) === 0);
      const link = element.closest('[aria-label]');
      if (link) link.setAttribute('aria-label', `Cart: ${count} item${Number(count) === 1 ? '' : 's'}`);
    });
  };

  const formatMoney = (cents, format = window.VardeNord?.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents || 0);
    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (!Number.isFinite(number)) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? parts.join(decimal) : parts[0];
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return formatWithDelimiters(value);
    let amount;
    switch (match[1]) {
      case 'amount_no_decimals': amount = formatWithDelimiters(value, 0); break;
      case 'amount_with_comma_separator': amount = formatWithDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': amount = formatWithDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': amount = formatWithDelimiters(value, 2, "'", '.'); break;
      default: amount = formatWithDelimiters(value, 2); break;
    }
    return format.replace(match[0], amount);
  };

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.sectionWrapper = this.closest('.shopify-section');
      if (this.dataset.sticky === 'true') this.sectionWrapper?.classList.add('header-section--sticky');
      if (this.dataset.transparent === 'true') this.sectionWrapper?.classList.add('header-section--transparent');
      this.lastScroll = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      this.onDetailsToggle = this.onDetailsToggle.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.querySelectorAll('[data-nav-details]').forEach((details) => details.addEventListener('toggle', this.onDetailsToggle));
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeDesktopMenus();
      });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      const current = window.scrollY;
      this.classList.toggle('is-scrolled', current > 12);
      if (this.dataset.sticky === 'true' && current > 180 && current > this.lastScroll + 8 && !this.classList.contains('is-menu-open')) {
        this.classList.add('is-hidden');
      } else if (current < this.lastScroll - 8 || current < 180) {
        this.classList.remove('is-hidden');
      }
      this.lastScroll = Math.max(current, 0);
    }

    onDetailsToggle(event) {
      if (!event.currentTarget.open) return;
      this.querySelectorAll('[data-nav-details][open]').forEach((details) => {
        if (details !== event.currentTarget) details.removeAttribute('open');
      });
    }

    closeDesktopMenus() {
      this.querySelectorAll('[data-nav-details][open]').forEach((details) => details.removeAttribute('open'));
    }
  }

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-predictive-input]');
      this.results = this.querySelector('[data-predictive-results]');
      this.form = this.querySelector('form');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.abortController = null;
      if (!this.input || !this.results) return;
      this.onInput = debounce(this.onInput.bind(this), 220);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.results.addEventListener('keydown', this.onResultsKeydown.bind(this));
      this.form?.addEventListener('reset', () => window.setTimeout(() => this.close(), 0));
    }

    async onInput() {
      const term = this.input.value.trim();
      this.clearButton?.toggleAttribute('hidden', !term);
      if (!term) {
        this.close();
        return;
      }
      this.setLoading(true);
      this.abortController?.abort();
      this.abortController = new AbortController();
      try {
        const route = window.VardeNord?.routes?.predictiveSearch || '/search/suggest';
        const url = `${route}?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article&resources[limit]=8&section_id=predictive-search`;
        const response = await fetch(url, { signal: this.abortController.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(response.statusText);
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = section ? section.innerHTML : html;
        this.input.setAttribute('aria-expanded', 'true');
        this.results.removeAttribute('hidden');
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      } finally {
        this.setLoading(false);
      }
    }

    onKeydown(event) {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const items = getFocusable(this.results);
      if (!items.length) return;
      event.preventDefault();
      (event.key === 'ArrowDown' ? items[0] : items[items.length - 1]).focus();
    }

    onResultsKeydown(event) {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const items = getFocusable(this.results);
      const index = items.indexOf(document.activeElement);
      if (index < 0) return;
      event.preventDefault();
      if (event.key === 'ArrowDown') (items[index + 1] || items[0]).focus();
      else if (index === 0) this.input.focus();
      else items[index - 1].focus();
    }

    setLoading(isLoading) {
      this.toggleAttribute('loading', isLoading);
      this.input.setAttribute('aria-busy', String(isLoading));
    }

    close() {
      this.abortController?.abort();
      this.results.innerHTML = '';
      this.results.setAttribute('hidden', '');
      this.input.setAttribute('aria-expanded', 'false');
      this.setLoading(false);
    }
  }

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="number"]');
      if (!this.input) return;
      this.querySelector('[name="minus"]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[name="plus"]')?.addEventListener('click', () => this.step(1));
    }

    step(direction) {
      if (direction > 0) this.input.stepUp();
      else this.input.stepDown();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.onChange = debounce(this.onChange.bind(this), 280);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
    }

    open(trigger) {
      openLayer(this, trigger);
    }

    close() {
      closeLayer(this);
    }

    onClick(event) {
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        const line = Number(remove.closest('[data-line]')?.dataset.line);
        if (line) this.changeLine(line, 0);
      }
    }

    onChange(event) {
      const quantity = event.target.closest('[data-cart-quantity]');
      if (quantity) {
        this.changeLine(Number(quantity.dataset.line), Math.max(0, Number(quantity.value)));
        return;
      }
      if (event.target.matches('#CartDrawer-Note')) this.updateNote(event.target.value);
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.querySelectorAll('button, input').forEach((element) => {
        if (isLoading) element.setAttribute('aria-busy', 'true');
        else element.removeAttribute('aria-busy');
      });
    }

    showError(message) {
      const error = this.querySelector('[data-cart-error]');
      if (!error) return;
      error.textContent = message || window.VardeNord?.strings?.cartError || 'Cart could not be updated.';
      error.hidden = !message;
    }

    async changeLine(line, quantity) {
      if (!line || !Number.isFinite(quantity)) return;
      this.setLoading(true);
      this.showError('');
      try {
        const response = await fetch(`${window.VardeNord?.routes?.cartChange || '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: ['cart-drawer'], sections_url: window.location.pathname })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message);
        this.renderContents(data);
        updateCartCount(data.item_count);
        announce(quantity === 0 ? 'Item removed from cart' : 'Cart updated');
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    async updateNote(note) {
      try {
        await fetch(`${window.VardeNord?.routes?.cartUpdate || '/cart/update'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note })
        });
      } catch (error) {
        this.showError(error.message);
      }
    }

    renderContents(data) {
      const html = data?.sections?.['cart-drawer'];
      if (!html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector('[data-cart-drawer]');
      if (replacement) this.innerHTML = replacement.innerHTML;
    }

    async refresh() {
      try {
        const route = window.VardeNord?.routes?.cart || '/cart';
        const response = await fetch(`${route}?section_id=cart-drawer`);
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const replacement = parsed.querySelector('[data-cart-drawer]');
        if (replacement) this.innerHTML = replacement.innerHTML;
        const countText = replacement?.querySelector('[data-cart-drawer-count]')?.textContent || '';
        const count = Number(countText.replace(/\D/g, ''));
        if (Number.isFinite(count)) updateCartCount(count);
      } catch (error) {
        this.showError(error.message);
      }
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.error = this.querySelector('[data-product-form-error]');
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      if (event.submitter && event.submitter.name && event.submitter.name !== 'add') return;
      if (window.VardeNord?.cartType !== 'drawer') return;
      event.preventDefault();
      if (this.submitButton?.disabled) return;
      this.setLoading(true);
      this.showError('');
      const formData = new FormData(this.form);
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
      try {
        const response = await fetch(`${window.VardeNord?.routes?.cartAdd || '/cart/add'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item.');
        const drawer = document.querySelector('[data-cart-drawer]');
        drawer?.renderContents(data);
        if (drawer) {
          const countText = drawer.querySelector('[data-cart-drawer-count]')?.textContent || '';
          const count = Number(countText.replace(/\D/g, ''));
          if (Number.isFinite(count)) updateCartCount(count);
          drawer.open(this.submitButton);
        }
        announce(`${data.product_title || 'Item'} added to cart`);
        this.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true, detail: data }));
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('loading', isLoading);
      this.submitButton.setAttribute('aria-busy', String(isLoading));
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  }

  const initRevealAnimations = (scope = document) => {
    const revealSelector = '[data-reveal]:not(.is-observed), .reveal-on-scroll .content-for-layout > .shopify-section:not(:first-child):not(.is-observed)';
    const elements = Array.from(scope.querySelectorAll(revealSelector));
    if (scope.matches?.(revealSelector)) elements.unshift(scope);
    if (!elements.length) return;
    if (prefersReducedMotion() || !window.IntersectionObserver) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, instance) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        instance.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    elements.forEach((element) => {
      element.classList.add('is-observed');
      observer.observe(element);
    });
  };

  const toggleVideo = (button) => {
    const container = button.closest('[data-media-container], .media-wrapper, section');
    const targetId = button.getAttribute('aria-controls');
    const video = (targetId && document.getElementById(targetId)) || container?.querySelector('video');
    if (!video) return;
    if (video.paused) {
      const promise = video.play();
      if (promise?.catch) promise.catch(() => {});
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', button.dataset.pauseLabel || 'Pause video');
      button.querySelector('[data-icon-play]')?.setAttribute('hidden', '');
      button.querySelector('[data-icon-pause]')?.removeAttribute('hidden');
    } else {
      video.pause();
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('aria-label', button.dataset.playLabel || 'Play video');
      button.querySelector('[data-icon-pause]')?.setAttribute('hidden', '');
      button.querySelector('[data-icon-play]')?.removeAttribute('hidden');
    }
  };

  const moveSlider = (button) => {
    const slider = button.closest('[data-slider]');
    const track = slider?.querySelector('[data-slider-track]') || slider?.querySelector('.slider-track');
    if (!track) return;
    const direction = button.dataset.direction === 'previous' || button.dataset.direction === 'prev' ? -1 : 1;
    const item = track.firstElementChild;
    const amount = item ? item.getBoundingClientRect().width + 20 : track.clientWidth * 0.8;
    track.scrollBy({ left: direction * amount, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  const openModal = (id, trigger) => {
    const modal = document.getElementById(id);
    if (modal) openLayer(modal, trigger);
  };

  const initGlobalListeners = () => {
    document.addEventListener('click', (event) => {
      const drawerTrigger = event.target.closest('[data-drawer-open]');
      if (drawerTrigger) {
        event.preventDefault();
        openLayer(document.getElementById(drawerTrigger.dataset.drawerOpen), drawerTrigger);
        return;
      }

      const cartTrigger = event.target.closest('[data-cart-open]');
      if (cartTrigger) {
        event.preventDefault();
        document.querySelector('[data-cart-drawer]')?.open(cartTrigger);
        return;
      }

      const searchTrigger = event.target.closest('[data-search-open]');
      if (searchTrigger) {
        event.preventDefault();
        const overlay = document.querySelector('[data-search-overlay]');
        openLayer(overlay, searchTrigger);
        return;
      }

      if (event.target.closest('[data-drawer-close], [data-search-close], [data-modal-close]')) {
        event.preventDefault();
        closeLayer();
        return;
      }

      const videoButton = event.target.closest('[data-video-control], [data-media-control]');
      if (videoButton) {
        event.preventDefault();
        toggleVideo(videoButton);
        return;
      }

      const sliderButton = event.target.closest('[data-slider-button]');
      if (sliderButton) {
        event.preventDefault();
        moveSlider(sliderButton);
        return;
      }

      const modalTrigger = event.target.closest('[data-modal-open]');
      if (modalTrigger) {
        event.preventDefault();
        openModal(modalTrigger.dataset.modalOpen, modalTrigger);
      }
    });

    document.querySelector('[data-overlay-backdrop]')?.addEventListener('click', () => closeLayer());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && state.activeLayer) closeLayer();
    });

    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-localization-select]')) event.target.form?.submit();
    });

    document.addEventListener('shopify:section:load', (event) => initRevealAnimations(event.target));
    document.addEventListener('shopify:section:unload', () => {
      if (state.activeLayer) closeLayer(state.activeLayer, false);
    });
  };

  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  window.VNTheme = { announce, closeLayer, debounce, formatMoney, openLayer, updateCartCount };
  initGlobalListeners();
  initRevealAnimations();
})();
